import React, { JSX, useMemo } from 'react'
import {
    createColumnHelper,
    rowPaginationFeature,
    tableFeatures,
    useTable,
    PaginationState,
} from '@tanstack/react-table'
import { Maybe } from '@Types'
import { PaginationBody, PaginationInfo } from '@Entities/pagination'
import { GetId } from '@App/interfaces/get-id'
import { CardWithHeader } from '@Components/Tables/CardResults/card-with-header'
import ExtendedOrCompactCardResults from '@Components/Tables/CardResults/ExtendedOrCompactCardResults'
import PaginationResultsBlock from '@Components/Navigations/PaginationResultsBlock'
import PaginationAndLimit from '@Components/Navigations/PaginationAndLimit'
import PointPreloader from '@Components/Preloaders/PointPreloader'
import { getNumTitles, INumTitles } from '@App/helpers/pagination/get-num-titles'

const features = tableFeatures({ rowPaginationFeature })

export interface ResultsCardTableProps<T extends GetId> {
    items: T[]
    /** Total number of results across all pages, not just `items.length`. */
    count: number
    paginationInfo: Maybe<PaginationInfo>
    limit: number
    page?: number
    loading: boolean
    /** Defaults to extended cards; pair with `compactCardType` to offer the compact view. */
    compact?: boolean
    onChangePage: (body: PaginationBody) => void
    onChangeLimit?: (newLimit: number) => void
    extendedCardType: React.ComponentType<T>
    compactCardType?: CardWithHeader<T>
    noResultsInfo?: React.ReactNode
    showCompactToggle?: boolean
    bulkActionControlSection?: JSX.Element
    outerDivClassName?: string
    paginationResultsMessage?: string
}

/**
 * Headless TanStack Table over any list of cards that expose `getId()`.
 *
 * The table owns the row model and the (server side) pagination state, while the existing
 * compact/extended cards keep rendering the rows. `WorkResultsCardTable` is the works flavour.
 */
const ResultsCardTable = <T extends GetId>(props: ResultsCardTableProps<T>) => {
    const {
        items,
        count,
        paginationInfo,
        limit,
        page,
        loading,
        compact,
        onChangePage,
        onChangeLimit,
        extendedCardType,
        compactCardType,
        noResultsInfo,
        showCompactToggle,
        bulkActionControlSection,
        outerDivClassName,
        paginationResultsMessage,
    } = props

    const currentPage = page ?? paginationInfo?.currentPage ?? 1

    // The cards render the rows, so the table only needs a column to hang the row model on.
    const columns = useMemo(() => {
        const columnHelper = createColumnHelper<typeof features, T>()
        return columnHelper.columns([columnHelper.display({ id: 'card' })])
    }, [])

    const table = useTable({
        features,
        columns,
        data: items,
        rowCount: count,
        manualPagination: true,
        getRowId: (item) => String(item.getId()),
        state: { pagination: { pageIndex: currentPage - 1, pageSize: limit } },
        onPaginationChange: (updater) => {
            const previous: PaginationState = { pageIndex: currentPage - 1, pageSize: limit }
            const next = typeof updater === 'function' ? updater(previous) : updater

            if (next.pageSize !== previous.pageSize) {
                onChangeLimit?.(next.pageSize)

                return
            }
            if (next.pageIndex !== previous.pageIndex) {
                onChangePage({ page: next.pageIndex + 1, limit: next.pageSize })
            }
        },
    })

    if (loading) {
        return <PointPreloader />
    }

    const rows = table.getRowModel().rows.map((row) => row.original)
    const numTitles: Maybe<INumTitles> = paginationInfo && getNumTitles(count, paginationInfo.currentPage, limit)

    return (
        <div className={outerDivClassName}>
            {bulkActionControlSection && <div className="bulk-action-control-section">{bulkActionControlSection}</div>}
            <PaginationResultsBlock
                totalResults={count}
                hideNoResults
                workNumTitles={numTitles}
                resultsMessage={paginationResultsMessage}
                showCompactToggle={showCompactToggle}
            />
            <ExtendedOrCompactCardResults
                data={rows}
                count={count}
                extendedCardType={extendedCardType}
                compact={compact}
                compactCardType={compactCardType}
                noResultsInfo={noResultsInfo}
            />
            {rows.length !== 0 && (
                <PaginationAndLimit
                    count={count}
                    limit={limit}
                    paginationInfo={paginationInfo}
                    onPageChange={(pagination) => table.setPageIndex((pagination.page ?? 1) - 1)}
                    onLimitChange={onChangeLimit && ((newLimit: number) => table.setPageSize(newLimit))}
                />
            )}
        </div>
    )
}

export default ResultsCardTable
